// src/contexts/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { User as FirebaseUser } from 'firebase/auth';
import { onAuthStateChanged, signOut as firebaseSignOut } from 'firebase/auth';
import { auth } from '../config/firebaseConfig';
import { User, LoginRequest, RegisterRequest } from '../types/chat';
import { authApi, tokenManager } from '../services/api';
import { useChatStore } from '../stores/chatStore';
import { useWorkflowStore } from '../stores/workflowStore';
import { useGuideStore } from '../stores/guideStore';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  register: (userData: RegisterRequest) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isGuest: boolean;
  continueAsGuest: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Wipe all session/chat data from both storages (prevents cross-user data leaks)
function clearSessionData() {
  const keys = [
    'currentSessionId', 'chatMessages', 'codeBlocks',
    'workflowSessionId', 'workflowMessages', 'workflowCodeBlocks',
    'lastCodemateSession', 'lastWorkflowSession',
  ];
  keys.forEach(k => { localStorage.removeItem(k); sessionStorage.removeItem(k); });
  localStorage.removeItem('guest_session_id');
  sessionStorage.removeItem('workflowUIState');

  // Clear prefixed caches (user_sessions:*, conversation:*)
  const toRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (key.startsWith('user_sessions:') || key.startsWith('conversation:'))) {
      toRemove.push(key);
    }
  }
  toRemove.forEach(k => localStorage.removeItem(k));
}

// Exchange Firebase ID token for a backend JWT
async function exchangeFirebaseToken(idToken: string): Promise<{ token: string; user?: User } | null> {
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  try {
    const res = await fetch(`${apiUrl}/auth/firebase`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id_token: idToken }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (!data.access_token) return null;
    return { token: data.access_token, user: data.user };
  } catch {
    return null;
  }
}

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGuest, setIsGuest] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Firebase auth state → ensure we have a valid backend JWT
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
      try {
        if (!firebaseUser) {
          setUser(null);
          setIsAuthenticated(false);
          setIsGuest(!!localStorage.getItem('guest_session_id'));
          setIsLoading(false);
          return;
        }

        setIsGuest(false);

        // 1. Get fresh Firebase token
        let idToken: string;
        try {
          idToken = await firebaseUser.getIdToken(true);
          sessionStorage.setItem('firebase_token', idToken);
        } catch {
          setIsAuthenticated(false);
          setIsLoading(false);
          return;
        }

        // 2. If we already have a JWT, restore instantly from cached user
        const existingJWT = localStorage.getItem('access_token');
        if (existingJWT) {
          const stored = localStorage.getItem('user');
          if (stored) {
            try { setUser(JSON.parse(stored)); } catch { /* ignore corrupt data */ }
          }
          setIsAuthenticated(true);
          setIsLoading(false);
          return;
        }

        // 3. No JWT — silently exchange Firebase token for one
        const result = await exchangeFirebaseToken(idToken);
        if (result) {
          localStorage.setItem('access_token', result.token);
          if (result.user) {
            localStorage.setItem('user', JSON.stringify(result.user));
            setUser(result.user);
          }
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }
        setIsLoading(false);
      } catch (err) {
        console.error('[AUTH] Auth state handler error:', err);
        setIsAuthenticated(false);
        setIsLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // Listen for token-saved events from FirebaseLoginForm (immediate auth update)
  useEffect(() => {
    const handleTokenSaved = (event: any) => {
      const token = localStorage.getItem('access_token');
      if (token) {
        console.log('[AUTH] Token saved event received — setting authenticated');
        if (event.detail?.user) {
          setUser(event.detail.user);
          localStorage.setItem('user', JSON.stringify(event.detail.user));
        }
        setIsAuthenticated(true);
        setIsGuest(false);
        setIsLoading(false);
      }
    };
    window.addEventListener('auth-token-saved', handleTokenSaved);
    return () => window.removeEventListener('auth-token-saved', handleTokenSaved);
  }, []);

  // Listen for token-invalid events from the API 401 interceptor
  useEffect(() => {
    const handleTokenInvalid = () => {
      setUser(null);
      setIsAuthenticated(false);
      setIsLoading(false);
      tokenManager.removeToken();
      localStorage.removeItem('user');
      sessionStorage.removeItem('firebase_token');
    };
    window.addEventListener('auth-token-invalid', handleTokenInvalid);
    return () => window.removeEventListener('auth-token-invalid', handleTokenInvalid);
  }, []);

  const login = async (credentials: LoginRequest) => {
    const response = await authApi.login(credentials);
    tokenManager.setToken(response.access_token);
    setIsAuthenticated(true);
    setUser(response.user);
    localStorage.setItem('user', JSON.stringify(response.user));
    clearSessionData();
  };

  const register = async (userData: RegisterRequest) => {
    const response = await authApi.register(userData);
    tokenManager.setToken(response.access_token);
    setIsAuthenticated(true);
    setUser(response.user);
    localStorage.setItem('user', JSON.stringify(response.user));
  };

  const logout = async () => {
    try { await firebaseSignOut(auth); } catch { /* Firebase sign-out is best-effort */ }
    authApi.logout();
    setUser(null);
    setIsGuest(false);
    setIsAuthenticated(false);
    clearSessionData();
    sessionStorage.removeItem('pro_trial_dismissed');
    // Clear Zustand stores
    useChatStore.getState().clearChat();
    useWorkflowStore.getState().clearWorkflow();
    useGuideStore.getState().clearGuide();
    window.location.href = '/login';
  };

  const continueAsGuest = () => {
    const guestSessionId = `guest_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    localStorage.setItem('guest_session_id', guestSessionId);
    localStorage.setItem('guest_executions_used', '0');
    localStorage.setItem('guest_messages_used', '0');
    setIsGuest(true);
    setIsLoading(false);
  };

  return (
    <AuthContext.Provider value={{
      user, isLoading, login, register, logout,
      isAuthenticated, isGuest, continueAsGuest,
    }}>
      {children}
    </AuthContext.Provider>
  );
};